import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Youtube } from 'lucide-react';

// Custom TikTok Icon SVG
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Snapchat Icon SVG
const SnapchatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105-.046-.345-.09-.705-.105-1.063-.016-1.275-.045-2.353.539-3.435C7.724.919 11.161.793 12.206.793"/>
  </svg>
);

const SocialMediaSidebar = () => {
  const socialLinks = [
    { 
      icon: Instagram, 
      url: 'https://instagram.com/omandronecarnival/', 
      label: 'Instagram', 
      color: '#E4405F',
      type: 'lucide'
    },
    { 
      icon: TikTokIcon, 
      url: 'https://tiktok.com/@omandronecarnival', 
      label: 'TikTok', 
      color: '#000000',
      type: 'custom'
    },
    { 
      icon: SnapchatIcon, 
      url: 'https://snapchat.com/add/droneoman_odc', 
      label: 'Snapchat', 
      color: '#FFFC00',
      type: 'custom'
    },
    { 
      icon: Youtube, 
      url: 'https://youtube.com/@omandronecarnival', 
      label: 'YouTube', 
      color: '#FF0000',
      type: 'lucide'
    },
  ];

  return (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 1 }}
      className="fixed right-0 top-1/3 -translate-y-1/2 z-50 hidden lg:block"
    >
      <div className="glass-effect border-l border-y border-primary/20 rounded-l-lg overflow-hidden">
        {socialLinks.map((social, index) => {
          const Icon = social.icon;
          return (
            <motion.a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 1.2 + index * 0.1 }}
              className="group relative block p-4 border-b border-primary/10 last:border-b-0 hover:bg-primary/10 transition-all duration-300"
              aria-label={social.label}
              data-testid={`social-${social.label.toLowerCase()}`}
            >
              <Icon className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              
              {/* Animated background */}
              <motion.div
                className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              />

              {/* Label on hover */}
              <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 px-4 py-2 bg-black/90 border border-primary/30 backdrop-blur-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 whitespace-nowrap">
                <span className="text-sm font-mono uppercase tracking-wider text-primary">{social.label}</span>
                <div className="absolute right-0 top-1/2 translate-x-1 -translate-y-1/2 w-2 h-2 bg-primary rotate-45" />
              </div>

              {/* Glow effect on hover */}
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: `0 0 20px ${social.color}40`,
                }}
              />
            </motion.a>
          );
        })}
      </div>
    </motion.div>
  );
};

export default SocialMediaSidebar;
